import React, { useState, useEffect } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ListChecks, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * CreateTaskModal — reusable across LoanDetail, ClientProfile, PayoffDetail, AlertsView.
 * Pre-fill any of related_loan_id / related_client_name / related_payoff_id / related_attorney_email.
 */
export default function CreateTaskModal({ open, onClose, prefill = {}, onCreated }) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    assignee_email: "",
    due_date: "",
    priority: "medium",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ title: prefill.title || "", description: prefill.description || "", assignee_email: prefill.assignee_email || "", due_date: prefill.due_date || "", priority: prefill.priority || "medium" });
    }
  }, [open, prefill.title, prefill.description, prefill.assignee_email, prefill.due_date, prefill.priority]);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        title: form.title.trim(),
        assignee_email: form.assignee_email || null,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        related_loan_id: prefill.related_loan_id || null,
        related_client_name: prefill.related_client_name || null,
        related_payoff_id: prefill.related_payoff_id || null,
        related_attorney_email: prefill.related_attorney_email || null,
      };
      const { data } = await api.post(`/operations/tasks`, body);
      toast.success("Task created");
      onCreated?.(data);
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4 bg-[#0A2540]/40 backdrop-blur-sm" onClick={onClose} data-testid="create-task-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3.5 border-b border-[#E5E7EB] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-[#0066FF]/10 flex items-center justify-center"><ListChecks className="w-4 h-4 text-[#0066FF]" /></div>
            <div>
              <div className="font-medium text-[#0A2540] text-sm">Create task</div>
              {(prefill.related_client_name || prefill.related_loan_id || prefill.related_payoff_id) && (
                <div className="text-[11px] text-[#6B7280]">
                  {prefill.related_client_name ? `Client: ${prefill.related_client_name}` : ""}
                  {prefill.related_loan_id ? ` · Loan: ${prefill.related_loan_id}` : ""}
                  {prefill.related_payoff_id ? ` · Payoff: ${prefill.related_payoff_id}` : ""}
                </div>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#0A2540]" data-testid="create-task-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <Label className="text-xs">Title *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Call attorney about payoff" data-testid="task-title" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional context…" data-testid="task-description" className="mt-1" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Assignee email</Label>
              <Input value={form.assignee_email} onChange={(e) => setForm({ ...form, assignee_email: e.target.value })} placeholder="admin@instabridgefunding.com" data-testid="task-assignee" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Due date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due" className="mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger data-testid="task-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-[#E5E7EB] flex justify-end gap-2 bg-[#F9FAFB]">
          <Button variant="ghost" onClick={onClose} data-testid="task-cancel">Cancel</Button>
          <Button onClick={create} disabled={saving || !form.title.trim()} className="bg-[#0066FF] hover:bg-[#0052CC]" data-testid="task-create-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ListChecks className="w-3.5 h-3.5 mr-1.5" />} Create task
          </Button>
        </div>
      </div>
    </div>
  );
}
